import { denormalisedResponseEntities, ensureOwnListing } from '../util/data';
import { storableError } from '../util/errors';
import { LISTING_STATE_DRAFT } from '../util/types';
import * as log from '../util/log';
import {
  getTransitionsNeedingProviderAttention,
  isEventTransition,
} from '../transactions/transaction';

import { authInfo } from './auth.duck';
import { stripeAccountCreateSuccess } from './stripeConnectAccount.duck';
import { util as sdkUtil } from '../util/sdkLoader';
import { postStripeDashboard, getSubUserInfo } from '../util/api';
import { loadConnectAndInitialize } from '@stripe/connect-js';

// ================ Action types ================ //

export const CURRENT_USER_SHOW_REQUEST = 'app/user/CURRENT_USER_SHOW_REQUEST';
export const CURRENT_USER_SHOW_SUCCESS = 'app/user/CURRENT_USER_SHOW_SUCCESS';
export const CURRENT_USER_SHOW_ERROR = 'app/user/CURRENT_USER_SHOW_ERROR';

export const CLEAR_CURRENT_USER = 'app/user/CLEAR_CURRENT_USER';

export const FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST';
export const FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_LISTINGS_ERROR =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_ERROR';

export const FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST';
export const FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS';
export const FETCH_CURRENT_USER_NOTIFICATIONS_ERROR =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_ERROR';

export const FETCH_CURRENT_USER_HAS_ORDERS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_REQUEST';
export const FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_ORDERS_ERROR = 'app/user/FETCH_CURRENT_USER_HAS_ORDERS_ERROR';

export const SEND_VERIFICATION_EMAIL_REQUEST = 'app/user/SEND_VERIFICATION_EMAIL_REQUEST';
export const SEND_VERIFICATION_EMAIL_SUCCESS = 'app/user/SEND_VERIFICATION_EMAIL_SUCCESS';
export const SEND_VERIFICATION_EMAIL_ERROR = 'app/user/SEND_VERIFICATION_EMAIL_ERROR';

export const CREATE_STRIPE_ACCOUNT_SESSION_REQUEST =
  'app/user/CREATE_STRIPE_ACCOUNT_SESSION_REQUEST';
export const CREATE_STRIPE_ACCOUNT_SESSION_SUCCESS =
  'app/user/CREATE_STRIPE_ACCOUNT_SESSION_SUCCESS';
export const CREATE_STRIPE_ACCOUNT_SESSION_ERROR = 'app/user/CREATE_STRIPE_ACCOUNT_SESSION_ERROR';

export const FETCH_NOTIFICATIONS_REQUEST = 'app/user/FETCH_NOTIFICATIONS_REQUEST';
export const FETCH_NOTIFICATIONS_SUCCESS = 'app/user/FETCH_NOTIFICATIONS_SUCCESS';
export const FETCH_NOTIFICATIONS_ERROR = 'app/user/FETCH_NOTIFICATIONS_ERROR';

export const MARK_NOTIFICATION_AS_READ = 'app/user/MARK_NOTIFICATION_AS_READ';

// ================ Reducer ================ //

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } = oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
      ? newCurrentUser
      : { id, type, attributes, ...oldRelationships, ...relationships };
};

const initialState = {
  currentUser: null,
  currentUserShowError: null,
  currentUserHasListings: false,
  currentUserHasListingsError: null,
  currentUserNotificationCount: 0,
  currentUserNotificationCountError: null,
  currentUserHasOrders: null, // This is not fetched unless unverified emails exist
  currentUserHasOrdersError: null,
  sendVerificationEmailInProgress: false,
  sendVerificationEmailError: null,
  notifications: [],
  /* Counted by the server, not derived from `notifications` — see
   * fetchNotificationData for why that distinction matters. */
  unreadMessageCount: 0,
  unreadSalesMessageCount: 0,
  notificationsInProgress: false,
  notificationsError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CURRENT_USER_SHOW_REQUEST:
      return { ...state, currentUserShowError: null };
    case CURRENT_USER_SHOW_SUCCESS:
      return { ...state, currentUser: mergeCurrentUser(state.currentUser, payload) };
    case CURRENT_USER_SHOW_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, currentUserShowError: payload };

    case CLEAR_CURRENT_USER:
      return {
        ...state,
        currentUser: null,
        currentUserShowError: null,
        currentUserHasListings: false,
        currentUserHasListingsError: null,
        currentUserNotificationCount: 0,
        currentUserNotificationCountError: null,
      };

    case FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST:
      return { ...state, currentUserHasListingsError: null };
    case FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS:
      return { ...state, currentUserHasListings: payload.hasListings };
    case FETCH_CURRENT_USER_HAS_LISTINGS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasListingsError: payload };

    case FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST:
      return { ...state, currentUserNotificationCountError: null };
    case FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS:
      return { ...state, currentUserNotificationCount: payload.transactions.length };
    case FETCH_CURRENT_USER_NOTIFICATIONS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserNotificationCountError: payload };

    case FETCH_CURRENT_USER_HAS_ORDERS_REQUEST:
      return { ...state, currentUserHasOrdersError: null };
    case FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS:
      return { ...state, currentUserHasOrders: payload.hasOrders };
    case FETCH_CURRENT_USER_HAS_ORDERS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasOrdersError: payload };

    case SEND_VERIFICATION_EMAIL_REQUEST:
      return {
        ...state,
        sendVerificationEmailInProgress: true,
        sendVerificationEmailError: null,
      };
    case SEND_VERIFICATION_EMAIL_SUCCESS:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
      };
    case SEND_VERIFICATION_EMAIL_ERROR:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
        sendVerificationEmailError: payload,
      };

    case CREATE_STRIPE_ACCOUNT_SESSION_REQUEST:
      return {
        ...state,
        connectInstance: null,
        createStripeAccountSessionInProgress: true,
        createStripeAccountSessionError: null,
      };
    case CREATE_STRIPE_ACCOUNT_SESSION_SUCCESS:
      return {
        ...state,
        createStripeAccountSessionInProgress: false,
        connectInstance: payload,
      };
    case CREATE_STRIPE_ACCOUNT_SESSION_ERROR:
      return {
        ...state,
        createStripeAccountSessionInProgress: false,
        createStripeAccountSessionError: payload,
        connectInstance: null,
      };
    case FETCH_NOTIFICATIONS_REQUEST:
      return { ...state, notificationsInProgress: true, notificationsError: null };
    case FETCH_NOTIFICATIONS_SUCCESS: {
      /* Keep the store's promise that this is always an array. Everything that
       * reads it calls array methods straight away, and a response that came back
       * empty would otherwise crash the topbar on the next render.
       *
       * The payload used to BE the array; it is now `{ notifications,
       * unreadMessageCount }`, because the count cannot be derived from the array
       * — see fetchNotificationData. Both shapes are accepted so an in-flight
       * dispatch from older code cannot blank the topbar. */
      const list = Array.isArray(payload) ? payload : payload?.notifications;
      return {
        ...state,
        notificationsInProgress: false,
        notifications: Array.isArray(list) ? list : [],
        unreadMessageCount: Array.isArray(payload) ? 0 : payload?.unreadMessageCount || 0,
        unreadSalesMessageCount: Array.isArray(payload)
          ? 0
          : payload?.unreadSalesMessageCount || 0,
      };
    }
    case FETCH_NOTIFICATIONS_ERROR:
      return { ...state, notificationsInProgress: false, notificationsError: payload };
    case MARK_NOTIFICATION_AS_READ:
      return {
        ...state,
        notifications: state.notifications.map(n =>
          n.id.uuid === payload
            ? { ...n, isUnread: false }
            : n
        ),
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const hasCurrentUserErrors = state => {
  const { user } = state;
  return (
    user.currentUserShowError ||
    user.currentUserHasListingsError ||
    user.currentUserNotificationCountError ||
    user.currentUserHasOrdersError
  );
};

export const verificationSendingInProgress = state => {
  return state.user.sendVerificationEmailInProgress;
};

// ================ Action creators ================ //

export const currentUserShowRequest = () => ({ type: CURRENT_USER_SHOW_REQUEST });

export const currentUserShowSuccess = user => ({
  type: CURRENT_USER_SHOW_SUCCESS,
  payload: user,
});

export const currentUserShowError = e => ({
  type: CURRENT_USER_SHOW_ERROR,
  payload: e,
  error: true,
});

export const clearCurrentUser = () => ({ type: CLEAR_CURRENT_USER });

const fetchCurrentUserHasListingsRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST,
});

export const fetchCurrentUserHasListingsSuccess = hasListings => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS,
  payload: { hasListings },
});

const fetchCurrentUserHasListingsError = e => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserNotificationsRequest = () => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST,
});

export const fetchCurrentUserNotificationsSuccess = transactions => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS,
  payload: { transactions },
});

const fetchCurrentUserNotificationsError = e => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserHasOrdersRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_REQUEST,
});

export const fetchCurrentUserHasOrdersSuccess = hasOrders => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS,
  payload: { hasOrders },
});

const fetchCurrentUserHasOrdersError = e => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_ERROR,
  error: true,
  payload: e,
});

export const sendVerificationEmailRequest = () => ({
  type: SEND_VERIFICATION_EMAIL_REQUEST,
});

export const sendVerificationEmailSuccess = () => ({
  type: SEND_VERIFICATION_EMAIL_SUCCESS,
});

export const sendVerificationEmailError = e => ({
  type: SEND_VERIFICATION_EMAIL_ERROR,
  error: true,
  payload: e,
});

export const createStripeAccountSessionRequest = () => ({
  type: CREATE_STRIPE_ACCOUNT_SESSION_REQUEST,
});

export const createStripeAccountSessionSuccess = instance => ({
  type: CREATE_STRIPE_ACCOUNT_SESSION_SUCCESS,
  payload: instance,
});

export const createStripeAccountSessionError = e => ({
  type: CREATE_STRIPE_ACCOUNT_SESSION_ERROR,
  error: true,
  payload: e,
});

export const markNotificationAsRead = id => ({
  type: MARK_NOTIFICATION_AS_READ,
  payload: id,
});

// ================ Thunks ================ //

/**
 * Swedish one-liners for the events the bell reports (BR-6).
 *
 * Kept as plain strings rather than translation keys because everything else in
 * this thunk builds its notification titles the same way, and mixing the two
 * would leave half the flyout translatable and half not. Moving all of them into
 * en.json is worth doing, but it is a change to every title, not just these.
 */
const EVENT_TITLES = {
  'transition/confirm-payment': 'Ny betald order',
  'transition/mark-delivered': 'Ordern har skickats',
  'transition/operator-mark-delivered': 'Ordern har skickats',
  'transition/mark-received': 'Köparen har kvitterat leveransen',
  'transition/mark-received-from-purchased': 'Köparen har kvitterat leveransen',
  'transition/accept': 'Bokningen accepterades',
  'transition/decline': 'Bokningen avvisades',
  'transition/operator-accept': 'Bokningen accepterades',
  'transition/operator-decline': 'Bokningen avvisades',
  'transition/cancel': 'Ordern avbröts',
  'transition/dispute': 'En tvist har öppnats',
  'transition/operator-dispute': 'En tvist har öppnats',
};

const eventTitleFor = transitionName => EVENT_TITLES[transitionName] || 'Ordern uppdaterades';

/**
 * Unread messages, for the envelope badge (BR-6 / AC-5).
 *
 * Reads the figure the server counted (`state.user.unreadMessageCount`), which
 * covers both roles: the envelope opens whichever inbox side the user was last
 * on, and "you have unread messages" is true either way. The seller dashboard's
 * card counts the provider side only, on purpose — that card is about sales.
 *
 * It is NOT derived from `state.user.notifications`. Browser testing caught that
 * mistake: the listings page reported one unread message and the envelope
 * reported none, because a notification only exists for transactions that
 * happened to land in one of three unrelated 100-row pages, and the unread one
 * had not. Counting flags on that list silently under-reports.
 */
export const unreadMessageCount = state => state?.user?.unreadMessageCount || 0;

/**
 * Events, for the bell badge (BR-6 / AC-5).
 *
 * The two counts now measure different things, which is the whole point: before
 * this, the envelope showed "transitions needing provider attention" and the bell
 * showed a mix of messages and every unopened sale. A seller comparing them got
 * two numbers that were both wrong and disagreed.
 */
export const eventNotificationCount = notifications =>
  (notifications || []).filter(n => n?.notificationType === 'ACTIVITY' && n?.isUnread).length;

export const fetchNotificationData = () => (dispatch, getState, sdk) => {
  dispatch({ type: FETCH_NOTIFICATIONS_REQUEST });

  // const transitionsNeedingAttention = getTransitionsNeedingProviderAttention();
  const rootUrl = process.env.REACT_APP_MARKETPLACE_ROOT_URL;

  /* 1 & 2. The two "genuinely has an unread message" queries.
   *
   * These filter on the metadata flags the app maintains when a message is sent
   * and clears when it is read — the same fields `/inbox/{orders,sales}
   * ?filter=unread` filters on, so any count derived from them agrees with the
   * page it links to.
   *
   * Both objects existed here but were never passed to a query: the Promise.all
   * below used ordersParams / salesParams instead, which only ask "does this
   * transaction have any message at all". They are wired up now and feed the
   * per-role flags attached further down. */
  const unreadOrdersParams = {
    only: 'order',
    meta_customerHasUnreadMessages: true,
    include: ['listing', 'provider'], // Removed profileImage
    perPage: 100,
  };

  const unreadSalesParams = {
    only: 'sale',
    meta_providerHasUnreadMessages: true,
    include: ['listing', 'customer'], // Removed profileImage
    perPage: 100,
  };

  // Only transactions that actually HAVE at least one message should produce a
  // "new message" notification. Without hasMessage:true every order/sale was
  // turned into a false "someone sent you a message" notification, even when
  // the transaction had no messages (only metadata flags like lastMessageDate).
  const ordersParams = {
    only: 'order',
    hasMessage: true,
    include: ['listing', 'provider', 'messages'],
    perPage: 100,
  };

  const salesParams = {
    only: 'sale',
    hasMessage: true,
    include: ['listing', 'customer', 'messages'],
    perPage: 100,
  };

  // 3. New Orders / Activity (Provider Role)
  const newActivityParams = {
    only: 'sale',
    include: ['listing'], // Only need listing for the title
    perPage: 100,
  };

  return Promise.all([
    sdk.transactions.query(ordersParams),
    sdk.transactions.query(salesParams),
    sdk.transactions.query(newActivityParams),
    /* Two extra reads, both narrow — they return only transactions already
     * flagged unread, which is a handful rather than the 100-row pages above. */
    sdk.transactions.query(unreadSalesParams),
    sdk.transactions.query(unreadOrdersParams),
  ])
    .then(responses => {
      // Helper to extract data safely from included entities
      const getEntity = (tx, type) => {
        const ref = tx.relationships[type]?.data;
        if (!ref) return null;
        // Search through all response data for the included entity
        return responses
          .flatMap(r => r.data.included || [])
          .find(e => e.id.uuid === ref.id.uuid && e.type === ref.type);
      };

      // --- Process Messages (Orders - You are Customer) ---
      const unreadOrders = responses[0].data.data.map(tx => {
        const listing = getEntity(tx, 'listing');
        const provider = getEntity(tx, 'provider');
        const lastVisit = tx.attributes?.metadata?.customerLastVisit;
        const providerLastMessageDate = tx.attributes?.metadata?.providerLastMessageDate;
        const createdAt = providerLastMessageDate || tx.attributes.lastMessageAt || tx.attributes.createdAt;
        const isUnread = !lastVisit || new Date(createdAt) > new Date(lastVisit);
        const name = provider?.attributes?.profile?.displayName || 'User';
        const listingTitle = listing?.attributes?.title || 'Listing';

        return {
          ...tx,
          notificationType: 'MESSAGE',
          isUnread,
          hasMessage: true,
          sortDate: createdAt,
          notificationTitle: `${name} har skickat ett nytt meddelande till dig på ${listingTitle}`,
          // Message notifications open the conversation in the inbox.
          url: `/inbox/orders/${tx.id.uuid}`,
        };
      });

      // --- Process Messages (Sales - You are Provider) ---
      const unreadSales = responses[1].data.data.map(tx => {
        const listing = getEntity(tx, 'listing');
        const customer = getEntity(tx, 'customer');
        const lastVisit = tx?.attributes?.metadata?.providerLastVisit;
        const customerLastMessageDate = tx.attributes?.metadata?.customerLastMessageDate;
        const createdAt = customerLastMessageDate || tx.attributes.lastMessageAt || tx.attributes.createdAt;
        const isUnread = !lastVisit || new Date(createdAt) > new Date(lastVisit);
        const name = customer?.attributes?.profile?.displayName || 'User';
        const listingTitle = listing?.attributes?.title || 'Listing';

        return {
          ...tx,
          notificationType: 'MESSAGE',
          isUnread,
          hasMessage: true,
          sortDate: createdAt,
          notificationTitle: `${name} har skickat ett nytt meddelande till dig på ${listingTitle}`,
          // Message notifications open the conversation in the inbox.
          url: `/inbox/sales/${tx.id.uuid}`,
        };
      });

      /* --- Process events (Activity) ---
       *
       * P25009-390 BR-6. This used to keep every sale the seller had not opened
       * since it last moved, and label each one "Ny transaktion på X". On
       * WHEELPLACE Test that is 165 of 265 transactions, and almost none of them
       * were news: 100 auto-cancelled, 83 expired without payment, 22 finished
       * their review period months ago. The bell counted all of it, which is why
       * it contradicted every other number on screen.
       *
       * `isEventTransition` is an allowlist of things a PERSON did — payment
       * taken, shipped, received, accepted, declined, cancelled, disputed. Timers
       * expiring are not events, and questions are messages, which the envelope
       * owns. That takes the bell from 165 to 4 on the same data.
       *
       * The title now names what happened rather than asserting a new
       * transaction, because "Ny transaktion" on a cancellation was itself
       * misleading. */
      const newActivity = responses[2].data.data
        .filter(tx => isEventTransition(tx.attributes))
        .filter(tx => {
          const lastVisit = tx.attributes.metadata?.providerLastVisit;
          /* Compare against when the transaction last MOVED, not when it was
           * created: an order placed in March and paid today is news today. The
           * old comparison against createdAt meant a seller who had ever opened
           * the thread never heard about it again. */
          const movedAt = tx.attributes.lastTransitionedAt || tx.attributes.createdAt;
          return !lastVisit || new Date(movedAt) > new Date(lastVisit);
        })
        .map(tx => {
          const listing = getEntity(tx, 'listing');
          const listingTitle = listing?.attributes?.title || 'Listing';
          const movedAt = tx.attributes.lastTransitionedAt || tx.attributes.createdAt;

          return {
            ...tx,
            notificationType: 'ACTIVITY',
            isUnread: true,
            hasMessage: false,
            sortDate: movedAt,
            notificationTitle: `${eventTitleFor(tx.attributes.lastTransition)} — ${listingTitle}`,
            url: `/sale/${tx.id.uuid}/details`,
          };
        });

      // --- Merge, De-duplicate, and Sort ---
      const combined = [...newActivity, ...unreadOrders, ...unreadSales];

      /* One transaction can be two things at once: a sale the seller has not
       * opened AND a conversation with an unread message. The de-duplication
       * below keeps one entry per transaction so the notification list does not
       * show the same order twice — but it keeps the FIRST match, and
       * newActivity is spread first, so the message facet was silently thrown
       * away.
       *
       * That made a genuinely unread message invisible to anything counting
       * messages, whenever the same transaction was also a new sale — i.e.
       * exactly when a buyer places an order and immediately asks a question.
       *
       * Recording the facts before collapsing the list keeps the single-entry
       * behaviour the notification menu relies on, while letting the seller
       * dashboard count orders and messages separately (P25009-385 BR-1). The
       * menu itself is unchanged: it still renders one row per transaction. */
      const unreadMessageTxIds = new Set(
        [...unreadOrders, ...unreadSales].filter(n => n.isUnread).map(n => n.id.uuid)
      );

      /* A stricter, per-role flag that agrees with the inbox.
       *
       * `isUnread` above is computed from providerLastVisit against a fallback
       * chain: customerLastMessageDate -> lastMessageAt -> the TRANSACTION's
       * createdAt. Measured against real data, every transaction it flagged fell
       * all the way through to that last fallback — no message dates existed at
       * all — so it was really reporting "this sale was never opened", including
       * sales from 2024, and calling it an unread message.
       *
       * `/inbox/sales?filter=unread` filters on meta_providerHasUnreadMessages,
       * so anything counting messages must use the same field or the number will
       * not match the page it links to. Keyed per role because a seller's own
       * purchases are a buyer concern and belong on the buyer surface.
       *
       * `isUnread` is left untouched: the notification menu's styling and the
       * bell's badge both read it, and reworking those is P25009-390 BR-6. */
      const providerUnreadTxIds = new Set(
        (responses[3]?.data?.data || []).map(tx => tx.id.uuid)
      );
      const customerUnreadTxIds = new Set(
        (responses[4]?.data?.data || []).map(tx => tx.id.uuid)
      );

      const uniqueNotifications = combined
        .filter((v, i, a) => a.findIndex(t => t.id.uuid === v.id.uuid) === i)
        .map(n => ({
          ...n,
          hasUnreadMessage: unreadMessageTxIds.has(n.id.uuid),
          hasUnreadMessageAsProvider: providerUnreadTxIds.has(n.id.uuid),
          hasUnreadMessageAsCustomer: customerUnreadTxIds.has(n.id.uuid),
        }));

      /* The unread-message COUNT, taken straight from the two narrow queries.
       *
       * It must not be derived by counting flags on `uniqueNotifications`, which
       * is what the first version of this did and what browser testing caught:
       * the listings page said "1 oläst meddelande" while the topbar envelope
       * said none.
       *
       * The flags can only be attached to notifications that exist, and the
       * notification list is built from three UNRELATED 100-row pages. A
       * transaction that is genuinely unread but sits beyond row 100 of the
       * `hasMessage` page never becomes a notification, so nothing carries its
       * flag and it vanishes from the count. On an account with a few hundred
       * transactions that is most of them.
       *
       * `meta.totalItems` is the server's own count of the unread query, so it is
       * exact regardless of page size — the 100-row ceiling cannot truncate it.
       * A Set is not needed: a user cannot be both provider and customer on one
       * transaction, so the two queries return disjoint rows. */
      const unreadSalesCount = responses[3]?.data?.meta?.totalItems || 0;
      const unreadOrdersCount = responses[4]?.data?.meta?.totalItems || 0;
      const unreadCount = unreadSalesCount + unreadOrdersCount;

      const sorted = uniqueNotifications.sort((a, b) => {
        // sortDate is precomputed per notification (last message date for
        // MESSAGE, createdAt for ACTIVITY), so sorting no longer depends on
        // parsing the URL (which now points at the inbox for messages).
        const dateA = a.sortDate || a.attributes.createdAt;
        const dateB = b.sortDate || b.attributes.createdAt;
        return new Date(dateB) - new Date(dateA);
      });
      dispatch({
        type: FETCH_NOTIFICATIONS_SUCCESS,
        payload: {
          notifications: sorted,
          unreadMessageCount: unreadCount,
          /* Provider side only, for the seller dashboard card and the listings
           * KPI — both are about the seller's own sales, and counting their
           * purchases there made the card disagree with the page it opens. */
          unreadSalesMessageCount: unreadSalesCount,
        },
      });
    })
    .catch(e => {
      dispatch({ type: FETCH_NOTIFICATIONS_ERROR, payload: storableError(e) });
    });
};

export const fetchCurrentUserHasListings = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasListingsRequest());
  const { currentUser } = getState().user;

  if (!currentUser) {
    dispatch(fetchCurrentUserHasListingsSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    // Since we are only interested in if the user has published
    // listings, we only need at most one result.
    authorId: currentUser.id.uuid,
    page: 1,
    perPage: 1,
  };

  return sdk.listings
    .query(params)
    .then(response => {
      const hasListings = response.data.data && response.data.data.length > 0;

      const hasPublishedListings =
        hasListings &&
        ensureOwnListing(response.data.data[0]).attributes.state !== LISTING_STATE_DRAFT;
      dispatch(fetchCurrentUserHasListingsSuccess(!!hasPublishedListings));
    })
    .catch(e => dispatch(fetchCurrentUserHasListingsError(storableError(e))));
};

export const fetchCurrentUserHasOrders = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasOrdersRequest());

  if (!getState().user.currentUser) {
    dispatch(fetchCurrentUserHasOrdersSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    only: 'order',
    page: 1,
    perPage: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const hasOrders = response.data.data && response.data.data.length > 0;
      dispatch(fetchCurrentUserHasOrdersSuccess(!!hasOrders));
    })
    .catch(e => dispatch(fetchCurrentUserHasOrdersError(storableError(e))));
};

// Notificaiton page size is max (100 items on page)
const NOTIFICATION_PAGE_SIZE = 100;

export const fetchCurrentUserNotifications = () => (dispatch, getState, sdk) => {
  const transitionsNeedingAttention = getTransitionsNeedingProviderAttention();
  if (transitionsNeedingAttention.length === 0) {
    // Don't update state, if there's no need to draw user's attention after last transitions.
    return;
  }

  const apiQueryParams = {
    only: 'sale',
    last_transitions: transitionsNeedingAttention,
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
  };

  dispatch(fetchCurrentUserNotificationsRequest());
  sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      const transactions = response.data.data;
      dispatch(fetchCurrentUserNotificationsSuccess(transactions));
    })
    .catch(e => dispatch(fetchCurrentUserNotificationsError(storableError(e))));
};

export const fetchCurrentUser = (params = null) => (dispatch, getState, sdk) => {
  dispatch(currentUserShowRequest());
  const { isAuthenticated } = getState().auth;

  if (!isAuthenticated) {
    // Make sure current user is null
    dispatch(currentUserShowSuccess(null));
    return Promise.resolve({});
  }

  const parameters = params || {
    include: ['profileImage', 'stripeAccount'],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      'variants.square-xsmall',
      'variants.square-xsmall2x',
    ],
    'imageVariant.square-xsmall': sdkUtil.objectQueryString({
      w: 40,
      h: 40,
      fit: 'crop',
    }),
    'imageVariant.square-xsmall2x': sdkUtil.objectQueryString({
      w: 80,
      h: 80,
      fit: 'crop',
    }),
  };

  return sdk.currentUser
    .show(parameters)
    .then(async response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.show response');
      }
      const currentUser = entities[0];

      // Save stripeAccount to store.stripe.stripeAccount if it exists
      if (currentUser.stripeAccount) {
        dispatch(stripeAccountCreateSuccess(currentUser.stripeAccount));
      }

      if (typeof window !== "undefined") {
        const subUserId = localStorage.getItem('subUserId');

        if (subUserId) {
          try {
            const subUser = await getSubUserInfo({ userId: subUserId });
            const { success, error, ...rest } = subUser;
            if (success) {
              currentUser.subUser = rest;
            } else {
              console.error(error);
            };
          } catch (error) {
            console.error(error);
          };
        };
      };

      // set current user id to the logger
      log.setUserId(currentUser.id.uuid);
      dispatch(currentUserShowSuccess(currentUser));
      return currentUser;
    })
    .then(currentUser => {
      dispatch(fetchCurrentUserHasListings());
      dispatch(fetchNotificationData());
      dispatch(fetchCurrentUserNotifications());
      if (!currentUser.attributes.emailVerified) {
        dispatch(fetchCurrentUserHasOrders());
      }

      // Make sure auth info is up to date
      dispatch(authInfo());
    })
    .catch(e => {
      // Make sure auth info is up to date
      dispatch(authInfo());
      log.error(e, 'fetch-current-user-failed');
      dispatch(currentUserShowError(storableError(e)));
    });
};

export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  if (verificationSendingInProgress(getState())) {
    return Promise.reject(new Error('Verification email sending already in progress'));
  }
  dispatch(sendVerificationEmailRequest());
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => dispatch(sendVerificationEmailSuccess()))
    .catch(e => dispatch(sendVerificationEmailError(storableError(e))));
};

export const createStripeAccountSession = () => async dispatch => {
  dispatch(createStripeAccountSessionRequest());
  try {
    const response = await postStripeDashboard();

    const instance = loadConnectAndInitialize({
      fetchClientSecret() {
        return Promise.resolve(response.data.client_secret);
      },
      publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
    });

    dispatch(createStripeAccountSessionSuccess(instance));
  } catch (e) {
    dispatch(createStripeAccountSessionError(storableError(e)));
  }
};
